import { z } from 'zod';

import { calendarDateSchema } from './employee';
import type { OvertimeRule, WeekTotals } from '../overtime';

/*
 * Timesheets — ROADMAP.md §10.2, business-rules.md §5.
 *
 * **Everything crossing this boundary is whole minutes.** Not decimal hours:
 * `41.25` invites a spreadsheet to multiply it by a rate and invites a float
 * while doing it. `formatMinutes` in `@shared/overtime` renders them at the
 * display edge, the same rule `formatCents` follows for money.
 *
 * **No pay amount appears anywhere in this file, and that is deliberate.** The
 * timesheet's job is to produce the right *hours*, split into regular and
 * overtime under the configured rule. Turning those into money means applying an
 * overtime multiplier, and what that multiplier is — and what it applies to — is
 * the owner's and their accountant's decision, not one to make in a view model.
 * business-rules.md §5 says so in as many words.
 */

/**
 * The period presets, matching the prototype's and the reports engine's.
 *
 * `custom` is what `from` and `to` are for; everything else is resolved on the
 * server against the business's own time zone, so "this week" is not whatever
 * the browser's clock thinks.
 */
export const TIMESHEET_PERIODS = [
  'this_week',
  'last_week',
  'this_month',
  'last_month',
  'custom',
] as const;

export type TimesheetPeriod = (typeof TIMESHEET_PERIODS)[number];

export const TIMESHEET_PERIOD_LABELS: Record<TimesheetPeriod, string> = {
  this_week: 'This week',
  last_week: 'Last week',
  this_month: 'This month',
  last_month: 'Last month',
  custom: 'Custom range',
};

export const timesheetQuerySchema = z
  .object({
    employeeId: z.uuid().optional(),
    period: z.enum(TIMESHEET_PERIODS).default('this_week'),
    from: calendarDateSchema.optional(),
    to: calendarDateSchema.optional(),
  })
  .refine((value) => value.period !== 'custom' || (value.from && value.to), {
    message: 'A custom range needs a start and an end',
    path: ['from'],
  });

export type TimesheetQuery = z.infer<typeof timesheetQuerySchema>;

/** One closed shift, as the screen shows it. */
export interface TimesheetShift {
  id: string;
  /** The local calendar day the shift *started* on. */
  date: string;
  clockInAt: string;
  clockOutAt: string;
  minutes: number;
  jobId: string | null;
  jobNumber: string | null;
  /** True when 10.3 has corrected it — the screen says so. */
  wasCorrected: boolean;

  /**
   * **What it used to say, and why.** ROADMAP.md §10.3 asks for "original values
   * remain visible", and a boolean saying an entry was touched is not that — the
   * office needs the old times in front of them to answer "why does this week
   * say 41 hours".
   *
   * All four are null on an entry nobody has corrected, which is almost all of
   * them.
   */
  originalClockInAt: string | null;
  originalClockOutAt: string | null;
  correctedByName: string | null;
  correctionNote: string | null;
}

/**
 * Somebody on the clock right now.
 *
 * **Surfaced so it can be closed**, which is 10.3's main job. The 16-hour sweep
 * in `worker/tasks.ts` tells admins "correct it on their timesheet" by name, and
 * until this field existed that link led to a screen with no way to do it.
 *
 * Its minutes are in no total — see `EmployeeTimesheet.hasOpenShift`.
 */
export interface OpenShiftView {
  id: string;
  clockInAt: string;
  jobNumber: string | null;
  /** Whole hours since it started, for "clocked in 19 hours ago". */
  hoursSoFar: number;
}

/** One person's hours across the period, already split into whole workweeks. */
export interface EmployeeTimesheet {
  employeeId: string;
  employeeName: string;
  isActive: boolean;

  weeks: WeekTotals[];
  shifts: TimesheetShift[];

  clockedMinutes: number;
  breakMinutes: number;
  paidMinutes: number;
  regularMinutes: number;
  overtimeMinutes: number;

  /**
   * Somebody who is on the clock right now.
   *
   * **Their running time is not in any total above.** An open shift has no end,
   * so counting the minutes so far would make a figure that changes every time
   * somebody refreshes — and a timesheet that disagrees with itself between two
   * looks is one nobody signs off. Named here so the screen can say a total is
   * still moving rather than silently understating it.
   */
  hasOpenShift: boolean;

  /** The open entry itself, when there is one, so the office can close it. */
  openShift: OpenShiftView | null;
}

/**
 * The four cards above the list — ROADMAP.md §10.2.
 *
 * **All four are numbers and a zero is `0`**, P1 and P10. The prototype's
 * equivalent cards fetch nothing and show `—` forever, which reads as zero while
 * meaning "unknown".
 */
export interface TimesheetTotals {
  clockedMinutes: number;
  regularMinutes: number;
  overtimeMinutes: number;
  shiftCount: number;
}

export interface TimesheetResponse {
  /** The whole workweeks actually covered — see `EmployeeTimesheet.weeks`. */
  from: string;
  to: string;
  /** What was asked for, before it was widened to whole weeks. */
  requestedFrom: string;
  requestedTo: string;

  totals: TimesheetTotals;
  employees: EmployeeTimesheet[];

  /**
   * Which rule produced these numbers, and its label.
   *
   * business-rules.md §5: "name the rule in the UI so nobody has to guess which
   * one produced the number." Sent from the server rather than assumed by the
   * screen, because the setting is the only thing that knows.
   */
  rule: OvertimeRule;
  ruleLabel: string;
  weeklyThresholdMinutes: number;
  weekStartsOn: number;
}
